/**
 *  @swagger
 *  definitions:
 *    RegattaDocumentParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Public:
 *          description: Public of the RegattaDocument
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaDocument
 *          type: string
 *        DocumentDate:
 *          description: Document Date of the RegattaDocument
 *          type: string
 *          format: date-time
 *        PublishDate:
 *          description: Publish Date of the RegattaDocument
 *          type: string
 *          format: date-time
 *        Status:
 *          description: Status of the RegattaDocument
 *          $ref: '#/components/enums/RegattaDocumentStatus'
 *        Content:
 *          description: Content of the RegattaDocument
 *          type: string
 *        File:
 *          description: File of the RegattaDocument
 *          type: string
 *        StatusContent:
 *          description: Status Content of the RegattaDocument
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaDocumentCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDocumentParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - DocumentDate
 *            - Status
 *
 *  @swagger
 *  definitions:
 *    RegattaDocumentQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaDocument
 *              type: string
 *        - $ref: '#/definitions/RegattaDocumentParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaDocumentUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDocumentQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaDocument:
 *      allOf:
 *        - $ref: '#/definitions/RegattaDocumentCreateParams'
 *        - $ref: '#/definitions/RegattaDocumentUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaDocument:
 *        allOf:
 *          - $ref: '#/definitions/RegattaDocument'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaDocument
 *                $ref: '#/components/schemas/Regatta'
 */

// Enums
import {
  RegattaDocumentStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: null | string;
  DocumentDate: Date;
  File: null | string;
  Name: string;
  Public: boolean;
  PublishDate?: Date | null;
  Status: RegattaDocumentStatus;
  StatusContent: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  DocumentDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  File: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  PublishDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaDocumentStatus)),
    validate: {
      notEmpty: true,
    },
  },
  StatusContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaDocument'> => {
  const RegattaDocument = stack.define(
    'RegattaDocument',
    fields(Sequelize),
    {
      tableName: 'RegattaDocuments',
    },
  );

  RegattaDocument.associate = (models: $ModelClasses) => {
    models.RegattaDocument.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaDocument.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaDocument;
});
